import { NoteEntity, EmployeesToDepartmentsEntity, ContractEntity, AgreementEntity, DepartmentEntity, CalendarEntryEntityTypeEnum, EmployeeEntity } from 'api/generated'
import * as ExcelJS from 'exceljs';
import { TFunction } from 'i18next';
import { translateContractType } from './utils';
import { decryptData } from './encryptionDecryptionAgreements';
import { fetchCurrentPaymentData, getMonthlyNets } from 'pages/employees/reportUtils';
import { addDays, differenceInDays, endOfMonth, format, isSameDay, startOfMonth } from 'date-fns';
import { calendarEntriesApi, roomApi, shiftApi } from 'api';
function formatDate(value: string): string {
    if (isDate(value)) {
        const date = new Date(value);
        return date.toLocaleDateString('it-IT');
    }
    return value;
}

function isDate(value: string): boolean {
    // Use a regex to check whether the string is in the format 'yyyy-MM-ddTHH:mm:ss.sssZ'
    const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
    return typeof value === 'string' && dateRegex.test(value);
}

export async function createReportForUser(employee: any, fileName: string, t: any) {
    // This array contains all the undesidered keys that we don't want include in the report
    const undesideredKeys = ['id', 'contract', 'bankingInformation', 'employeesToDepartments', 'employeeId', 'filePath', 'roomId', 'statusId',
        'buildingId', 'maxTenants', 'floor', 'accessMode', 'bathrooms', 'hasKitchen', 'notes', 'room', 'agreements'];
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('report.sheetTitle'));
    // Set header design for the report
    reportHeaderForUserDesign(worksheet, t);
    worksheet.getColumn('A').width = 22;
    const columnB = worksheet.getColumn('B');
    worksheet.getColumn('E').width = 22;
    const columnF = worksheet.getColumn('F');
    worksheet.getColumn('I').width = 22;
    const columnJ = worksheet.getColumn('J');
    worksheet.getColumn('M').width = 22;
    const columnN = worksheet.getColumn('N');
    worksheet.getColumn('Q').width = 30;
    const columnR = worksheet.getColumn('R');
    // Set width for dynamic columns
    columnB.width = 0;
    columnF.width = 0;
    columnJ.width = 0;
    columnN.width = 0;
    columnR.width = 10;
    // Transforms data into arrays, if employee.relation is empty assign the empty array to the keys array
    const keysEmployee = Object.keys(employee);
    const keysBanking = employee.bankingInformation ? Object.keys(employee.bankingInformation) : [];
    const keysContract = employee.employeesToSeasons[0].contract ? Object.keys(employee.employeesToSeasons[0].contract) : [];
    const keysRoom = employee.employeesToSeasons[0].room ? Object.keys(employee.employeesToSeasons[0].room) : [];
    const { notes } = employee;
    const agreements = employee.employeesToSeasons[0].contract?.agreements ?? [];
    const maxKeysLength = Math.max(keysEmployee.length, keysBanking.length, keysContract.length, keysRoom.length, agreements.length);
    // Create filtered keys for employee, banking information and contract
    const keyEmployeeFiltered = keysEmployee.filter((keyEmployee) => !undesideredKeys.includes(keyEmployee));
    const keyBankingFiltered = keysBanking.filter((keyBanking) => !undesideredKeys.includes(keyBanking));
    const keyContractFiltered = keysContract.filter((keyContract) => !undesideredKeys.includes(keyContract));
    const keyRoomFiltered = keysRoom.filter((keysRoom) => !undesideredKeys.includes(keysRoom));
    // Print to report all the filtered keys + values 
    for (let i = 0; i < maxKeysLength; i++) {
        const agreement = agreements[i];
        const createdAt = agreement ? formatDate(agreement.createdAt) : '';
        const startedOn = agreement ? formatDate(agreement.startedOn) : '';
        const endedOn = agreement ? formatDate(agreement.endedOn) : '';

        // Function to extract the value from the composed key
        const extractValue = (compositeKey?: { name: string } | null) => {
            return compositeKey?.name ?? compositeKey;
        };

        const roomValue = extractValue(employee.employeesToSeasons[0].room?.[keyRoomFiltered[i]]);

        const agreementValidDate = startedOn || endedOn ? `${t('employees.agreements.excel.valid')} ${startedOn} ${t('employees.agreements.excel.to')} ${endedOn}` : '';

        worksheet.addRow([
            keyEmployeeFiltered[i],
            formatDate(employee[keyEmployeeFiltered[i]]),
            '',
            '',
            keyBankingFiltered[i],
            formatDate(employee.bankingInformation?.[keyBankingFiltered[i]]),
            '',
            '',
            keyContractFiltered[i],
            formatDate(employee.employeesToSeasons.contract?.[keyContractFiltered[i]]),
            '',
            '',
            keyRoomFiltered[i],
            roomValue,
            '',
            '',
            agreementValidDate,
            createdAt
        ]);

        worksheet.addRow([]);
    }

    // Calculating column size. Employee must be define since the button to genereate the report only exist if the employee exist 
    const employeeLength = getMaxLength(Object.values(employee));
    const bankingLength = employee.bankingInformation ? getMaxLength(Object.values(employee.bankingInformation)) : 0;
    const contractLength = employee.employeesToSeasons[0].contract ? getMaxLength(Object.values(employee.employeesToSeasons[0].contract)) : 0;
    const roomLength = employee.employeesToSeasons[0].room ? getMaxLength(Object.values(employee.employeesToSeasons[0].room)) : 0;


    columnB.width = Math.min(Math.max(columnB.width, employeeLength + 6), 50);
    columnF.width = Math.min(Math.max(columnF.width, bankingLength + 6), 50);
    columnJ.width = Math.min(Math.max(columnJ.width, contractLength + 6), 50);
    columnN.width = Math.min(Math.max(columnN.width, roomLength + 6), 50);

    // Set report Design
    reportForUserDesign(worksheet);
    await addLogoImageToReport(worksheet, workbook);

    const worksheetNotes = workbook.addWorksheet(t('report.sheetTitleNotes'));
    worksheetNotes.getColumn('A').width = 22;
    worksheetNotes.getColumn('D').width = 50;

    worksheetNotes.addRow([t('report.createdAtNotes'), '', '', t('report.textNotes')]);
    worksheetNotes.addRow([]);
    notes.forEach((note: NoteEntity) => {
        worksheetNotes.addRow([formatDate(note.createdAt), '', '', note.text]);
        worksheetNotes.addRow([]);
    });
    notesDesign(worksheetNotes);

    downloadWorkbook(workbook, fileName);
}

// Function to calculate the max length of values array
function getMaxLength(obj: any): number {
    if (obj === null || obj === undefined) {
        return 0;
    }
    if (typeof obj !== "object") {
        return obj.toString().length;
    }
    const values = Object.values(obj);
    const normalKeysLength = Math.max(...values.map((value) => getMaxLength(value)));
    const subKeysLength = Math.max(...Object.keys(obj).map((key) => getMaxLength(obj[key])));

    return Math.max(normalKeysLength, subKeysLength);
}

// Function to create the header design for the report
function reportHeaderForUserDesign(worksheet: ExcelJS.Worksheet, t: any) {
    worksheet.addRow([t('report.title')])
    worksheet.mergeCells('A1:R2');
    worksheet.addRow([]);
    worksheet.addRow([t('report.columnTitle.general'), '', '', '', t('report.columnTitle.banking'), '', '', '', t('report.columnTitle.contract'),
        '', '', '', t('housing.collectionName'), '', '', '', t('employees.agreements.title'),
    t('employees.agreements.dataTable.createdOn')]);
    worksheet.addRow([]);
}

// Function to create the design for the report
function reportForUserDesign(worksheet: ExcelJS.Worksheet) {
    const boldCells = ['A4', 'E4', 'I4', 'M4', 'Q4'];
    worksheet.getCell('A1').fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'DCE6F1' },
    }
    // Cell Formatting
    worksheet.eachRow((row: any) => {
        row.alignment = { wrapText: true }
        row.eachCell((cell: any, colNumber: any) => {
            if (cell.value !== '') {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'DCE6F1' },
                }
            };
            cell.font = Object.assign({}, { name: 'Montserrat', size: 9, bold: boldCells.includes(cell._address) });
            // Align values on the right hand side
            if (colNumber === 2 || colNumber === 6 || colNumber === 10 || colNumber === 14) {
                cell.alignment = { horizontal: 'right' };
            }
        });
    });
    // Set title format
    worksheet.getCell('A1').font = { name: 'Montserrat', size: 12, bold: true };
    worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };
}

async function addLogoImageToReport(worksheet: ExcelJS.Worksheet, workbook: ExcelJS.Workbook) {
    const imageSrc = "/static/logo/logo_onestaff_new.png";
    const response = await fetch(imageSrc);
    const buffer = await response.arrayBuffer();
    const logo = workbook.addImage({
        buffer,
        extension: 'png',
    });
    worksheet.addImage(logo, {
        tl: { col: 0.3, row: 0.1 },  // Coordinates
        ext: { width: 60, height: 40 }, // Image size
    });
}

function downloadWorkbook(workbook: ExcelJS.Workbook, fileName: string) {
    // Generate the Excel file as a blob
    workbook.xlsx.writeBuffer().then(function (buffer: ArrayBuffer) {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        // Create a link element
        const downloadLink = document.createElement('a');

        // Use the blob to set the href for the link
        const url = window.URL.createObjectURL(blob);
        downloadLink.href = url;

        // Set the download attribute for the link to specify the filename
        downloadLink.download = fileName;

        // Append the link to the document, trigger a click, and then remove it
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    });
}



/**
 * Build a per-employee map of human-readable housing strings from the new
 * `EmployeeRoomAssignment` model (the same data the room-assignment tableau
 * uses). Replaces the legacy `EmployeeToSeason.room` direct relation which
 * only ever captured a single room per season without dates.
 *
 * Returns Map<employeeId, { buildings: string; rooms: string }> where:
 *   - buildings  → comma-separated unique building names
 *   - rooms      → "Room (dd/MM/yyyy – dd/MM/yyyy); Room (…)" — one entry per
 *                  assignment, in chronological order
 */
async function fetchHousingByEmployee(seasonId: number | undefined) {
    const result = new Map<number, { buildings: string; rooms: string }>();
    if (!seasonId) return result;
    try {
        const res = await roomApi.findRoomAssignments(String(seasonId), '');
        const assignments = (res.data ?? []) as any[];
        const grouped = new Map<number, any[]>();
        for (const a of assignments) {
            const list = grouped.get(a.employeeId) ?? [];
            list.push(a);
            grouped.set(a.employeeId, list);
        }
        const fmt = (iso: string) => new Date(iso).toLocaleDateString('it-IT');
        grouped.forEach((items: any[], employeeId: number) => {
            items.sort((x: any, y: any) => new Date(x.startDate).getTime() - new Date(y.startDate).getTime());
            const buildingNames: string[] = Array.from(new Set(
                items
                    .map((a: any) => a.room?.building?.name)
                    .filter((n: string | undefined): n is string => !!n),
            ));
            const roomParts = items.map((a: any) => {
                const roomName = a.room?.name ?? `#${a.roomId}`;
                return `${roomName} (${fmt(a.startDate)} – ${fmt(a.endDate)})`;
            });
            result.set(employeeId, {
                buildings: buildingNames.join(', '),
                rooms: roomParts.join('; '),
            });
        });
    } catch (err) {
        console.error('Failed to fetch room assignments for report:', err);
    }
    return result;
}

export async function createReportForAllUsers(users: any, fileName: string, t: any, encryptionKey?: string, seasonId?: number) {
    // Defines column keys
    const keys = [
        'firstName', 'lastName', 'taxcode', 'email', 'phone', 'gender', 'dateOfBirth', 'countryOfBirth', 'provinceOfBirth', 'townshipOfBirth',
        'contract.typeName', 'contract.startedOn', 'contract.endedOn', 'contract.netThisMonth', 'contract.agreementsMonthly',
        'bankingInformation.iban', 'bankingInformation.bicSwift', 'role',
        'contract.isFoodIncluded', 'isHoused',
        'buildingName', 'roomName',
        'segments', 'departments', 'workPositions', 'additonalInformations.shirtSize', 'additonalInformations.pantsSize'
    ];
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('report.sheetTitleMultiEmployees'));
    reportHeaderForAllUsersDesign(worksheet, keys, t);


    // Pull housing data from the new EmployeeRoomAssignment model so the
    // report reflects what's shown in the room-assignment tableau (per-period
    // assignments with start/end dates), not the legacy single-room
    // EmployeeToSeason.room relation.
    const housingByEmployee = await fetchHousingByEmployee(seasonId);

    users.sort((a: any, b: any) => {
        if (a.firstName < b.firstName) return -1;
        if (a.firstName > b.firstName) return 1;
        if (a.lastName < b.lastName) return -1;
        if (a.lastName > b.lastName) return 1;
        return 0;
    }).forEach((userData: any) => {
        const departmentLenght = userData.employeesToDepartments.length;

        // Housing comes EXCLUSIVELY from the new EmployeeRoomAssignment
        // model (the same data the room-assignment tableau renders).
        // The legacy `EmployeeToSeason.room` direct relation is ignored on
        // purpose — an employee without an assignment in the new model has
        // empty housing columns, matching what the user sees in the tableau.
        const newHousing = housingByEmployee.get(userData.id);
        userData.buildingName = newHousing?.buildings ?? '';
        userData.roomName = newHousing?.rooms ?? '';

        if (userData.employeesToSeasons[0]) {
            if (userData.employeesToSeasons[0].contract) {
                userData.contract = userData.employeesToSeasons[0].contract;
                userData.contract.typeName = translateContractType(userData.contract.legalContractType)
                // if agreements is defined
                if (userData.contract.agreements) {
                    const monthStart = startOfMonth(new Date());
                    const monthEnd = endOfMonth(new Date());
                    // find agreements that overlap with this month
                    const overlappingAgreements = (userData.contract.agreements as AgreementEntity[]).filter(
                        agreement => {
                            const startedOn = new Date(agreement.startedOn);
                            const endedOn = agreement.endedOn ? new Date(agreement.endedOn) : undefined;
                            return +startedOn < +monthEnd && (!endedOn || +endedOn > +monthStart);
                        }
                    );
                    userData.contract.netThisMonth = 0;

                    overlappingAgreements.forEach((agreement) => {
                        let decryptedNet = 0;
                        // decrypt if needed
                        if (agreement.net_daily_encrypted !== null && encryptionKey) {
                            const net = decryptData(agreement.net_daily_encrypted, encryptionKey);
                            if (net !== '') {
                                decryptedNet = Number(net);
                            }
                        }
                        else
                            decryptedNet = agreement.net_daily ?? 0;

                        // compute days differences between start of agreement and start of month
                        const daysFromStart = differenceInDays(new Date(agreement.startedOn), monthStart);
                        // compute days differences between end of month and end of agreement
                        const daysFromEnd = agreement.endedOn ? differenceInDays(monthEnd, new Date(agreement.endedOn)) : 0;

                        // if these differences are negative agreement starts or ends out of this month, so on days computing should be 0
                        const normalizedDaysFromStart = daysFromStart > 0 ? daysFromStart : 0;
                        const normalizedDaysFromEnd = daysFromEnd > 0 ? daysFromEnd : 0;

                        // compute total days of month minus difference in days to get how many days agreement has in this month
                        const days = monthEnd.getDate() - normalizedDaysFromStart - normalizedDaysFromEnd;
                        // add product to total net of this month
                        userData.contract.netThisMonth += decryptedNet * days;

                    })

                    // Calculate agreementsMonthly: all agreements multiplied by 30 with their time periods
                    const agreements = userData.contract.agreements as AgreementEntity[];
                    const hasMultipleAgreements = agreements.length > 1;
                    const agreementsMonthlyParts: string[] = [];
                    agreements.forEach((agreement) => {
                        let decryptedNet = 0;
                        // decrypt if needed
                        if (agreement.net_daily_encrypted !== null && encryptionKey) {
                            const net = decryptData(agreement.net_daily_encrypted, encryptionKey);
                            if (net !== '') {
                                decryptedNet = Number(net);
                            }
                        }
                        else
                            decryptedNet = agreement.net_daily ?? 0;

                        const monthlyNet = decryptedNet * 30;
                        if (hasMultipleAgreements) {
                            const startDate = format(new Date(agreement.startedOn), 'dd.MM.yyyy');
                            const endDate = agreement.endedOn ? format(new Date(agreement.endedOn), 'dd.MM.yyyy') : t('report.present');
                            agreementsMonthlyParts.push(`${monthlyNet.toFixed(2)} € (${startDate} - ${endDate})`);
                        } else {
                            agreementsMonthlyParts.push(`${monthlyNet.toFixed(2)} €`);
                        }
                    });
                    userData.contract.agreementsMonthly = agreementsMonthlyParts.join('; ');
                } else {
                    userData.contract.netThisMonth = 0;
                    userData.contract.agreementsMonthly = '';
                }
            } else {
                // If no contract, initialize empty values
                if (!userData.contract) {
                    userData.contract = {};
                }
                userData.contract.netThisMonth = 0;
                userData.contract.agreementsMonthly = '';
            }
        } else {
            // If no employeesToSeasons, initialize empty values
            if (!userData.contract) {
                userData.contract = {};
            }
            userData.contract.netThisMonth = 0;
            userData.contract.agreementsMonthly = '';
        }

        userData.departments = '';
        userData.segments = '';
        userData.workPositions = '';
        userData.employeesToDepartments.forEach((employeeToDepartment: EmployeesToDepartmentsEntity, index: number) => {
            if (employeeToDepartment.department?.segment?.name && !(userData.segments as string).includes(employeeToDepartment.department?.segment?.name)) {
                userData.segments += employeeToDepartment.department?.segment?.name;
                if (departmentLenght - index > 1)
                    userData.segments += ', ';
            }

            userData.departments += employeeToDepartment.department?.name;
            if (departmentLenght - index > 1)
                userData.departments += ', ';

            if (departmentLenght > 1)
                userData.workPositions += employeeToDepartment.department?.name + ': ' + employeeToDepartment.workPosition?.name;
            else
                userData.workPositions += employeeToDepartment.workPosition?.name;

            if (departmentLenght - index > 1)
                userData.workPositions += ', ';

        })

        userData.isHoused = Boolean(userData.roomName);
        worksheet.addRow(keys.map((keyItem) => {
            return formatDate(keyItem.split('.').reduce((acc, key) => acc?.[key], userData));
        }));

        reportForAllUsersDesign(worksheet.lastRow, keys, worksheet);
    });

    await addLogoImageToReport(worksheet, workbook);
    downloadWorkbook(workbook, fileName);
}

function translateKeys(t: TFunction<"translation", undefined>, key?: string) {
    switch (key) {
        case 'firstName':
            return t('employees.generalInformation.firstName');
        case 'lastName':
            return t('employees.generalInformation.lastName');
        case 'taxcode':
            return t('employees.generalInformation.taxcode');
        case 'email':
            return t('auth.email');
        case 'phone':
            return t('employees.generalInformation.phone');
        case 'dateOfBirth':
            return t('employees.generalInformation.dateOfBirth.label');
        case 'countryOfBirth':
            return t('employees.generalInformation.country');
        case 'provinceOfBirth':
            return t('employees.generalInformation.province');
        case 'townshipOfBirth':
            return t('employees.generalInformation.township');
        case 'typeName':
            return t('employees.contract.contractType.label');
        case 'startedOn':
            return t('employees.calendar.entry.startedOn');
        case 'endedOn':
            return t('employees.calendar.entry.endedOn');
        case 'net':
            return t('employees.agreements.field.monthly.net');
        case 'netThisMonth':
            return t('employees.report.cost.dueThisMonth');
        case 'agreementsMonthly':
            return t('employees.agreements.field.monthly.net');
        case 'iban':
            return t('employees.bank.iban');
        case 'bicSwift':
            return t('employees.bank.bicswift');
        case 'role':
            return t('employees.generalInformation.role');
        case 'isFoodIncluded':
            return t('employees.contract.foodIncluded');
        case 'isHoused':
            return t('excel.isHoused');
        case 'roomName':
            return t('room.housing.title');
        case 'buildingName':
            return t('housing.itemName');
        case 'segments':
            return t('department.segments');
        case 'departments':
            return t('departments.collectionName');
        case 'workPositions':
            return t('departments.workPosition.label');
        case 'due':
            return t('report.paymentsTable.toBePaid');
        case 'bankName':
            return t('employees.bank.bankName');
        case 'gender':
            return t("employees.generalInformation.gender.label");
        case 'shirtSize':
            return t("employees.additionalInformation.shirt");
        case 'pantsSize':
            return t("employees.additionalInformation.pants");
        case 'notes':
            return t('employees.notes.title');
        case 'experience':
            return t('expLevels.itemName');
        default:
            return key;


    }
}

function toUTC(date: Date) {
    const dateStr = format(date, 'yyyy-MM-dd');
    return new Date(`${dateStr}T00:00:00.000Z`);
}
function reportHeaderForAllUsersDesign(worksheet: ExcelJS.Worksheet, keys: string[], t: any, isWireReport?: boolean) {
    worksheet.addRow([t('report.title')])
    const lastColumn = getColumnLetter(keys.length);
    isWireReport ? worksheet.mergeCells('A1:L2') : worksheet.mergeCells(`A1:${lastColumn}2`);
    worksheet.addRow(keys.map(key => translateKeys(t, key.split('.').pop()))).font = { name: 'Montserrat', size: 9, bold: true };
    worksheet.getCell('A1').font = { name: 'Montserrat', size: 12, bold: true };
    worksheet.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };
    worksheet.getCell('A1').fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'DCE6F1' },
    }
}

// Convert column number to Excel column letter (1 -> A, 27 -> AA, etc.)
function getColumnLetter(columnNumber: number): string {
    let result = '';
    while (columnNumber > 0) {
        columnNumber--;
        result = String.fromCharCode(65 + (columnNumber % 26)) + result;
        columnNumber = Math.floor(columnNumber / 26);
    }
    return result;
}

// Indent and format rows
function reportForAllUsersDesign(row: any, keys: string[], worksheet: ExcelJS.Worksheet) {
    for (let i = 1; i <= keys.length; i++) {
        const columnName = getColumnLetter(i);
        worksheet.getColumn(columnName).width = 22;
        const cell = worksheet.getCell(`${columnName}${row.number}`);
        if (row._number % 2 === 0) {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'DCE6F1' },
            };
        }
        cell.font = {
            name: 'Montserrat',
            size: 9,
        };
        cell.alignment = {
            horizontal: 'left',
            vertical: 'bottom',
            wrapText: false,
        }
    }
}

function notesDesign(worksheet: ExcelJS.Worksheet) {
    const columnNotesD = worksheet.getColumn('D');
    columnNotesD.alignment = { wrapText: true };
    const boldCells = ['A1', 'D1'];
    boldCells.forEach(cellRef => {
        const cell = worksheet.getCell(cellRef);
        cell.font = { bold: true };
    });

    // Cell Formatting
    worksheet.eachRow((row: any) => {
        row.alignment = { wrapText: true }
        row.eachCell((cell: any) => {
            if (cell.value !== '') {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'DCE6F1' },
                }
            };
        })
    });
}



export async function createExportCalendar(
    employees: Array<{ employeeId: number; name: string }>,
    startDate: Date,
    endDate: Date,
    fileName: string,
    t: TFunction<'translation', undefined>,
    seasonId: number
) {
    const workedHours = (await shiftApi.hoursForPeriodDaily(startDate.toISOString(), endDate.toISOString(), seasonId)).data;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('employees.calendar.events'), {
        views: [{ state: 'frozen', xSplit: 1, ySplit: 1 }]
    });
    const duration = differenceInDays(endDate, startDate);
    calendarHeader(worksheet, startDate, endDate);

    const headerRow = worksheet.getRow(1);
    headerRow.getCell(duration + 3).value = 'TOTAL';

    for (const employee of employees.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
    })) {
        const { employeeId, name } = employee;
        const values = [name];
        const entries = (
            await calendarEntriesApi.findAll(employeeId.toString(), seasonId)
        ).data.filter((entry) => {
            return (
                +addDays(new Date(entry.startedOn), -1) <= +endDate &&
                +addDays(new Date(entry.endedOn), 1) >= +startDate
            );
        });
        let totalRowMinutes = 0;
        for (let i = 0; i <= duration; i++) {
            const currentDate = addDays(new Date(startDate), i);
            const foundHours = workedHours.find(
                (wh) => wh.employee.id === employeeId && isSameDay(new Date(wh.date), currentDate)
            );
            if (foundHours) {
                totalRowMinutes += foundHours.totalMinutes;
                const hrs = Math.floor(foundHours.totalMinutes / 60);
                const mins = foundHours.totalMinutes % 60;
                values.push(`${hrs}h ${mins}m`);
                continue;
            }
            const value =
                entries.find((entry) => {
                    const start = toUTC(new Date(entry.startedOn));
                    const end = toUTC(new Date(entry.endedOn));
                    return (
                        +currentDate >= +start && +currentDate <= +end
                    ) || isSameDay(currentDate, start) || isSameDay(currentDate, end);
                })?.type ?? 'WORK';
            values.push(translateCalendarEntries(value, t));
        }
        const totalHrs = Math.floor(totalRowMinutes / 60);
        const totalMins = totalRowMinutes % 60;
        values.push(`${totalHrs}h ${totalMins}m`);
        worksheet.addRow(values);
    }

    if (worksheet.lastColumn && worksheet.lastRow) {
        worksheet.addConditionalFormatting({
            ref: 'A1:' + worksheet.lastColumn.letter + worksheet.lastRow.number,
            rules: [
                {
                    priority: 1,
                    type: 'containsText',
                    operator: 'containsText',
                    text: translateCalendarEntries('HALF_DAY', t),
                    style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: '00EAFF' } } }
                },
                {
                    priority: 1,
                    type: 'containsText',
                    operator: 'containsText',
                    text: translateCalendarEntries('DAY_OFF', t),
                    style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFCDC2' } } }
                },
                {
                    priority: 1,
                    type: 'containsText',
                    operator: 'containsText',
                    text: translateCalendarEntries('VACATION', t),
                    style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'BDF0DA' } } }
                },
                {
                    priority: 1,
                    type: 'containsText',
                    operator: 'containsText',
                    text: translateCalendarEntries('ILL', t),
                    style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFC0D3' } } }
                },
                {
                    priority: 1,
                    type: 'containsText',
                    operator: 'containsText',
                    text: translateCalendarEntries('UNPAID_LEAVE', t),
                    style: { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'D2BCEB' } } }
                }
            ]
        });
    }

    worksheet.eachRow((row) => {
        row.height = 25;
    });

    downloadWorkbook(workbook, fileName);
}



const translateCalendarEntries = (type: string, t: TFunction<"translation", undefined>) => {

    switch (type) {
        case "WORK":
            return t('employees.calendar.types.work');
        case "DAY_OFF":
            return t('employees.calendar.types.day_off');
        case "VACATION":
            return t('employees.calendar.types.vacation');
        case "HALF_DAY":
            return t('employees.calendar.types.half_day');
        case "ILL":
            return t('employees.calendar.types.ill');
        case "UNPAID_LEAVE":
            return t('employees.calendar.types.unpaid_leave');
        default:
            return type;

    }

}

function calendarHeader(worksheet: ExcelJS.Worksheet, startDate: Date, endDate: Date) {
    const dates: any = [''];

    // name column styling
    const firstColumn = worksheet.getColumn('A');
    firstColumn.width = 30;
    firstColumn.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'DCE6F1' },
    };

    firstColumn.font = {
        bold: true
    }


    const columnLetters: number[] = [2];

    for (let currentDate = new Date(startDate); +currentDate < +endDate || isSameDay(currentDate, endDate); currentDate = addDays(currentDate, 1)) {
        dates.push(new Date(Date.UTC((currentDate.getFullYear()), (currentDate.getMonth()), currentDate.getDate())));
        let columnName = '';

        columnLetters.forEach((letter) => {
            columnName = String.fromCharCode(64 + letter) + columnName;
        })
        worksheet.getColumn(columnName).width = 22;

        if (columnLetters.every(value => value === 26)) {
            for (let i = 0; i < columnLetters.length; i++)
                columnLetters[i] = 1;
            columnLetters.push(1);
        }
        else {
            columnLetters[columnLetters.findIndex(value => value < 26)]++;
        }
    }


    worksheet.addRow(dates);

    // header styling

    const row = worksheet.getRow(1);

    row.font = { name: 'Montserrat', size: 9, bold: true };

    row.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'DCE6F1' },
    };
}

export async function createWireTransfersReportForAllUsers(users: any, fileName: string, t: any, encryptionKey: string, date: Date) {
    // Check if the user has unlocked the encryption key in the frontend (green key indicator)
    // The encryptionKey parameter comes from useEncryption() hook - it's empty string if not unlocked
    const isKeyUnlocked = encryptionKey && encryptionKey.trim() !== '';
    
    // Check if there's any encrypted data and if the key can decrypt it
    let hasEncryptedData = false;
    let canDecryptData = false;
    
    if (isKeyUnlocked) {
        // Try to find an agreement with encrypted data to test if the key can decrypt it
        for (const userData of users) {
            const agreements = userData.employeesToSeasons?.at(0)?.contract?.agreements as AgreementEntity[];
            if (agreements && agreements.length > 0) {
                for (const agreement of agreements) {
                    // Test decryption on net_daily_encrypted if it exists
                    if (agreement.net_daily_encrypted) {
                        hasEncryptedData = true;
                        const decrypted = decryptData(agreement.net_daily_encrypted, encryptionKey);
                        // If decryption succeeds (returns non-empty string), the key is correct
                        if (decrypted !== '') {
                            canDecryptData = true;
                            break;
                        }
                    }
                }
                if (canDecryptData) break;
            }
        }
    }
    
    // Show encrypted columns ONLY if:
    // 1. Key is unlocked (green icon) AND
    // 2. (There's no encrypted data OR the key can decrypt the data)
    const shouldShowEncryptedColumns = isKeyUnlocked && (!hasEncryptedData || canDecryptData);
    
    // Conditionally define column keys based on encryption key validity
    const baseKeys = ['firstName', 'lastName', 'bankName', 'iban', "bicSwift"];
    const conditionalKeys = shouldShowEncryptedColumns ? ["due", "net", "notes"] : [];
    const finalKeys = [...baseKeys, ...conditionalKeys, "startedOn", "endedOn", "experience", "departments"];
    
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('report.sheetTitleMultiEmployees'));
    reportHeaderForAllUsersDesign(worksheet, finalKeys, t, true);

    // for instead of forEach needed to avoid sync issues
    // order by last name
    for (const userData of users.sort((a: any, b: any) => {
        if (a.firstName < b.firstName) return -1;
        if (a.firstName > b.firstName) return 1;
        if (a.lastName < b.lastName) return -1;
        if (a.lastName > b.lastName) return 1;
        return 0;
    })) {

        if (userData) {

            let totalEarned = 0;

            let totalPaid = 0;
/*
            const setTotalEarned = (x: number) => { totalEarned = x };

            const setTotalPaid = (x: number) => { totalPaid = x };
*/
            const contract = userData.employeesToSeasons?.at(0)?.contract as ContractEntity;
            if (!contract)
                continue;

            const agreements = userData.employeesToSeasons?.at(0)?.contract.agreements as AgreementEntity[];
            const departments: DepartmentEntity[] = userData.employeesToDepartments
                .map((empToDep: EmployeesToDepartmentsEntity) => { return empToDep.department })
                .filter((element: DepartmentEntity) => { return element !== null || element !== undefined });

            const seasonId = contract.legalContractType === 'UNLIMITED' ? null : userData.employeesToSeasons?.at(0).seasonId as number;

            userData.bankName = userData.bankingInformation?.bankName;
            userData.iban = userData.bankingInformation?.iban;
            userData.bicSwift = userData.bankingInformation?.bicSwift;
            userData.startedOn = contract.startedOn;
            userData.endedOn = contract.endedOn;
            userData.experience = contract.levelName;
            userData.departments = departments.map((dep) => { return dep.name }).toString();
            
            // Only calculate and set these values if encryption key is valid or there's no encrypted data
            if (shouldShowEncryptedColumns) {
                const data = await fetchCurrentPaymentData(userData.id, seasonId, contract.id, encryptionKey, date);
                totalEarned = data?.totalEarned ?? 0;
                totalPaid = data?.totalPaid ?? 0;
                const monthlyNets = getMonthlyNets(agreements, date, encryptionKey);
                const due = (totalEarned - totalPaid);
                userData.due = due + ' €';
                userData.net = monthlyNets.map((net) => { return net.toFixed(2) + ' €' }).toString();
                userData.notes = agreements
                    .map((agr) => { return agr.notes })
                    .filter((element) => { return element !== null && element !== "" })
                    .toString();
            }

            worksheet.addRow(finalKeys.map((keyItem) => {
                return formatDate(keyItem.split('.').reduce((acc, key) => acc?.[key], userData));
            }));
            reportForAllUsersDesign(worksheet.lastRow, finalKeys, worksheet);
        }
    };

    wireTransfersDesign(worksheet, date, t);

    await addLogoImageToReport(worksheet, workbook);
    downloadWorkbook(workbook, fileName);

}

// Indent and format net rows
function wireTransfersDesign(worksheet: ExcelJS.Worksheet, date: Date, t: TFunction<"translation", undefined>) {
    const lastRow = worksheet.addRow([t('report.selectDate') + ': ' + date.toLocaleDateString('it-IT')]);

    lastRow.font = { name: 'Montserrat', size: 8, bold: true };
    lastRow.getCell(2).merge(lastRow.getCell(1));
    lastRow.eachCell((cell) => {
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'DCE6F1' }
        }
    });
}


// Converts a CSS color string (#RGB or #RRGGBB) to an ExcelJS ARGB [header, data-tint] pair.
// The data-tint is the same hue at ~15% opacity on white.
function cssColorToArgbPair(cssColor: string): [string, string] {
    let hex = cssColor.replace('#', '');
    if (hex.length === 3) hex = hex[0]+hex[0]+hex[1]+hex[1]+hex[2]+hex[2];
    hex = hex.toUpperCase().substring(0, 6);
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const toHex2 = (n: number) => Math.min(255, Math.max(0, Math.round(n))).toString(16).padStart(2, '0').toUpperCase();
    return [
        `FF${hex}`,
        `FF${toHex2(r * 0.15 + 255 * 0.85)}${toHex2(g * 0.15 + 255 * 0.85)}${toHex2(b * 0.15 + 255 * 0.85)}`,
    ];
}

// Default event type CSS colors — must match calendar.tsx `items` array
const EVENT_TYPE_CSS: Record<string, string> = {
    DAY_OFF:      '#FF5C38',   // theme.palette.warning.main
    HALF_DAY:     '#00EAFF',
    VACATION:     '#0CC97A',   // theme.palette.success.main
    ILL:          '#FF316F',   // theme.palette.error.main
    UNPAID_LEAVE: '#6C25BE',
};

const DEPT_REPORT_CONCURRENCY = 15;

async function fetchEventCountsBatch(
    employees: EmployeeEntity[],
    seasonStart: Date,
    seasonEnd: Date,
    seasonId: number,
    onProgress?: (current: number, total: number) => void,
): Promise<Map<number, Record<string, number>>> {
    const results = new Map<number, Record<string, number>>();
    let done = 0;
    const total = employees.length;

    for (let i = 0; i < employees.length; i += DEPT_REPORT_CONCURRENCY) {
        const chunk = employees.slice(i, i + DEPT_REPORT_CONCURRENCY);
        await Promise.all(chunk.map(async (employee) => {
            const contract = employee.employeesToSeasons?.[0]?.contract;
            const contractStart = contract?.startedOn ? new Date(contract.startedOn) : null;
            const contractEnd   = contract?.endedOn   ? new Date(contract.endedOn)   : null;
            const periodStart   = contractStart && contractStart > seasonStart ? contractStart : seasonStart;
            const periodEnd     = contractEnd   && contractEnd   < seasonEnd   ? contractEnd   : seasonEnd;
            try {
                const data = (await calendarEntriesApi.findEventsPerPeriodOfTime(
                    format(periodStart, 'yyyy-MM-dd'),
                    format(addDays(periodEnd, 1), 'yyyy-MM-dd'),
                    employee.id,
                    seasonId.toString(),
                )).data as Record<string, number>;
                results.set(employee.id, data);
            } catch {
                results.set(employee.id, {});
            }
            done++;
            if (onProgress) onProgress(done, total);
        }));
    }
    return results;
}

export async function createDepartmentReport(
    employees: EmployeeEntity[],
    seasonStartDate: string,
    seasonEndDate: string,
    seasonId: number,
    fileName: string,
    t: TFunction<'translation', undefined>,
    onProgress?: (current: number, total: number) => void,
) {
    const seasonStart = new Date(seasonStartDate);
    const seasonEnd   = new Date(seasonEndDate);

    // Fetch custom calendar entry types
    const customTypes = (await calendarEntriesApi.getAllCustomTypes()).data;

    // Default event type columns
    const defaultEventTypes = [
        { key: CalendarEntryEntityTypeEnum.DayOff,      label: t('employees.calendar.types.day_off') },
        { key: CalendarEntryEntityTypeEnum.HalfDay,     label: t('employees.calendar.types.half_day') },
        { key: CalendarEntryEntityTypeEnum.Vacation,    label: t('employees.calendar.types.vacation') },
        { key: CalendarEntryEntityTypeEnum.Ill,         label: t('employees.calendar.types.ill') },
        { key: CalendarEntryEntityTypeEnum.UnpaidLeave, label: t('employees.calendar.types.unpaid_leave') },
    ];
    const customEventTypes = customTypes.map((ct) => ({ key: `custom-${ct.id}`, label: ct.name, colour: ct.colour || '#4299E1' }));
    const allEventTypes: Array<{ key: string; label: string; colour?: string }> = [...defaultEventTypes, ...customEventTypes];

    // ── 1. Fetch all event counts in parallel (unique employees) ──
    const uniqueEmployees = Array.from(
        new Map(employees.map((e) => [e.id, e])).values()
    );
    const eventCountsMap = await fetchEventCountsBatch(
        uniqueEmployees, seasonStart, seasonEnd, seasonId, onProgress,
    );

    // ── 2. Group employees by department ──
    const departmentMap = new Map<string, EmployeeEntity[]>();
    for (const employee of employees) {
        const departments = employee.employeesToDepartments;
        if (departments && departments.length > 0) {
            for (const empToDep of departments) {
                const deptName = empToDep.department?.name ?? t('report.departmentReport.noDepartment');
                if (!departmentMap.has(deptName)) departmentMap.set(deptName, []);
                departmentMap.get(deptName)?.push(employee);
            }
        } else {
            const noDeptLabel = t('report.departmentReport.noDepartment');
            if (!departmentMap.has(noDeptLabel)) departmentMap.set(noDeptLabel, []);
            departmentMap.get(noDeptLabel)?.push(employee);
        }
    }

    // ── 3. Build workbook ──
    const workbook = new ExcelJS.Workbook();

    const headerLabels = [
        t('report.departmentReport.firstName'),
        t('report.departmentReport.lastName'),
        t('report.departmentReport.contractStart'),
        t('report.departmentReport.contractEnd'),
        t('report.departmentReport.durationDays'),
        t('report.departmentReport.durationWeeks'),
        ...allEventTypes.map((et) => et.label),
    ];
    const colCount = headerLabels.length;

    // Column meta: [alignment, minWidth]
    const colMeta: Array<{ alignment: 'left' | 'center' | 'right'; minWidth: number }> = [
        { alignment: 'left',   minWidth: 14 },  // First name
        { alignment: 'left',   minWidth: 14 },  // Last name
        { alignment: 'center', minWidth: 13 },  // Contract start
        { alignment: 'center', minWidth: 13 },  // Contract end
        { alignment: 'center', minWidth: 11 },  // Days
        { alignment: 'center', minWidth: 11 },  // Weeks
        ...allEventTypes.map(() => ({ alignment: 'center' as const, minWidth: 10 })),
    ];

    // Helper: get event column [headerArgb, dataArgb] — custom types use their own colour
    const getEventColor = (idx: number): [string, string] => {
        const eventType = allEventTypes[idx];
        if (!eventType) return cssColorToArgbPair('#4299E1');
        const css = eventType.colour ?? EVENT_TYPE_CSS[eventType.key];
        return css ? cssColorToArgbPair(css) : cssColorToArgbPair('#4299E1');
    };

    for (const deptName of Array.from(departmentMap.keys())) {
        const deptEmployees = departmentMap.get(deptName) ?? [];
        const sheetName = deptName.replace(/[[\]*?/\\:]/g, '').substring(0, 31);

        const worksheet = workbook.addWorksheet(sheetName, {
            views: [{ state: 'frozen', xSplit: 0, ySplit: 2 }],
        });

        // ── Title row ──
        const titleRow = worksheet.addRow([deptName]);
        titleRow.height = 28;
        worksheet.mergeCells(1, 1, 1, colCount);
        const titleCell = worksheet.getCell('A1');
        titleCell.font      = { name: 'Montserrat', size: 13, bold: true, color: { argb: 'FF1A2B4A' } };
        titleCell.alignment = { vertical: 'middle', horizontal: 'center' };
        titleCell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDCE6F1' } };
        titleCell.border    = { bottom: { style: 'medium', color: { argb: 'FF7B9FC7' } } };

        // ── Header row ──
        const headerRow = worksheet.addRow(headerLabels);
        headerRow.height = 22;
        headerRow.eachCell((cell, colIdx) => {
            const isNameCol = colIdx <= 2;
            const isEventCol = colIdx > 6;
            const eventIdx = colIdx - 7;

            let headerColor = 'FF2B4C7E';  // dark blue for fixed cols
            if (isEventCol) headerColor = getEventColor(eventIdx)[0];
            else if (colIdx > 2) headerColor = 'FF3A6099'; // medium blue for date/duration cols

            cell.font      = { name: 'Montserrat', size: 9, bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: headerColor } };
            cell.alignment = {
                vertical: 'middle',
                horizontal: isNameCol ? 'left' : 'center',
                wrapText: true,
            };
            cell.border = {
                top:    { style: 'thin', color: { argb: 'FFAAAAAA' } },
                left:   { style: 'thin', color: { argb: 'FFAAAAAA' } },
                bottom: { style: 'medium', color: { argb: 'FF555555' } },
                right:  { style: 'thin', color: { argb: 'FFAAAAAA' } },
            };
        });

        // ── Sort employees alphabetically ──
        const sortedEmployees = [...deptEmployees].sort((a, b) => {
            const cmp = a.lastName.localeCompare(b.lastName);
            return cmp !== 0 ? cmp : a.firstName.localeCompare(b.firstName);
        });

        // ── Data rows ──
        // accumulators for totals row
        const eventTotals = allEventTypes.map(() => 0);

        sortedEmployees.forEach((employee, rowIdx) => {
            const isEven = rowIdx % 2 === 0;
            const contract      = employee.employeesToSeasons?.[0]?.contract;
            const contractStart = contract?.startedOn ? new Date(contract.startedOn) : null;
            const contractEnd   = contract?.endedOn   ? new Date(contract.endedOn)   : null;

            let durationDays: number | string = '';
            let durationWeeks: number | string = '';
            if (contractStart && contractEnd) {
                durationDays  = differenceInDays(contractEnd, contractStart) + 1;
                durationWeeks = Number((durationDays / 7).toFixed(2));
            }

            const eventCounts = eventCountsMap.get(employee.id) ?? {};
            const eventValues = allEventTypes.map((et, i) => {
                const v = eventCounts[et.key] ?? 0;
                eventTotals[i] += v;
                return v;
            });

            const row = worksheet.addRow([
                employee.firstName,
                employee.lastName,
                contractStart ? contractStart.toLocaleDateString('it-IT') : '',
                contractEnd   ? contractEnd.toLocaleDateString('it-IT')   : '',
                durationDays,
                durationWeeks,
                ...eventValues,
            ]);

            row.height = 18;
            row.eachCell((cell, colIdx) => {
                const isEventCol  = colIdx > 6;
                const eventIdx    = colIdx - 7;

                let bgColor = isEven ? 'FFFAFBFE' : 'FFFFFFFF';
                if (isEventCol) {
                    const [, dataColor] = getEventColor(eventIdx);
                    bgColor = isEven ? dataColor : 'FFFFFFFF';
                }

                cell.font      = { name: 'Montserrat', size: 9 };
                cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
                cell.alignment = { vertical: 'middle', horizontal: colMeta[colIdx - 1].alignment };
                cell.border = {
                    top:    { style: 'hair',  color: { argb: 'FFD0D0D0' } },
                    left:   { style: 'thin',  color: { argb: 'FFD0D0D0' } },
                    bottom: { style: 'hair',  color: { argb: 'FFD0D0D0' } },
                    right:  { style: 'thin',  color: { argb: 'FFD0D0D0' } },
                };
            });
        });

        // ── Totals row ──
        const totalsRow = worksheet.addRow([
            t('report.departmentReport.total'),
            '',
            '',
            '',
            '',
            '',
            ...eventTotals,
        ]);
        totalsRow.height = 20;
        totalsRow.eachCell((cell, colIdx) => {
            const isEventCol = colIdx > 6;
            const eventIdx   = colIdx - 7;
            const headerColor = isEventCol ? getEventColor(eventIdx)[0] : 'FF2B4C7E';

            cell.font      = { name: 'Montserrat', size: 9, bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill      = { type: 'pattern', pattern: 'solid', fgColor: { argb: headerColor } };
            cell.alignment = { vertical: 'middle', horizontal: colIdx <= 2 ? 'left' : 'center' };
            cell.border = {
                top:    { style: 'medium', color: { argb: 'FF555555' } },
                left:   { style: 'thin',   color: { argb: 'FFAAAAAA' } },
                bottom: { style: 'medium', color: { argb: 'FF555555' } },
                right:  { style: 'thin',   color: { argb: 'FFAAAAAA' } },
            };
        });

        // ── Column widths ──
        worksheet.columns.forEach((column, idx) => {
            const meta = colMeta[idx];
            let maxLength = meta?.minWidth ?? 10;
            column.eachCell?.({ includeEmpty: true }, (cell) => {
                const v = cell.value != null ? String(cell.value) : '';
                maxLength = Math.max(maxLength, v.length + 2);
            });
            column.width = Math.min(maxLength, 28);
        });

        await addLogoImageToReport(worksheet, workbook);
    }

    downloadWorkbook(workbook, fileName);
}